import { Component, OnInit, Input } from '@angular/core';
import { TabComponent } from '../tab-component';
import { ActivatedRoute, Route, Router } from '@angular/router';
import { SupplierStateService } from 'app/profile/services/supplierstate.service';
import { I18nService } from 'app/shared/services/i18n.service';
import { SupplierDocumentService } from 'app/profile/services/supplier-document.service';
import { SupplierState } from 'app/profile/store/state/supplierstate.state';
import { Store, select } from '@ngrx/store';
import { TslDialog, PageEvent, TslSort } from '@tesla/ng-tds';
import { GetContractsByCode, ClearSupplierDetailsByCode, GetSupplierProfileCurrentUser } from 'app/profile/store/app.actions';
import { SelectSupplierContracts, SelectCurrentUser, getGeneralSettings } from 'app/profile/store/app.selector';
import { Contract } from 'app/profile/models/supplier.model';
import { BehaviorSubject } from 'rxjs';
import { DocumentType } from 'app/profile/models/supplier.model'
import * as FileSaver from 'file-saver';
import { UserDetails, SupplierContactMap } from 'app/profile/models/user.model';
import { GeneralSettings } from 'app/profile/models/generic.model';
import { AppSettings } from '../../../app.settings';

@Component({
    selector: 'app-other-document',
    templateUrl: './other-document.component.html',
    styleUrls: ['./other-document.component.scss']
})
export class OtherDocumentComponent implements OnInit, TabComponent {
    @Input() data: any;
    supplierCode: string;
    settings: GeneralSettings;

    contracts: Contract[];
    total: number;
    wsmUrl: string;

    datasource: BehaviorSubject<Array<Contract>> = new BehaviorSubject([]);

    /** Columns displayed in the table. Columns IDs can be added, removed, or reordered. */
    displayedColumns = ['category', 'name', 'start', 'expiry', 'signature', 'status'];
    pageSizeOptions: number[] = [10, 25, 50, 100, 250];
    pageIndex = 0;
    pageSize = 10;

    types: DocumentType[] = [];

    userDetails: UserDetails = null;
    isPrimary: boolean = false;
    isProfileAdmin: boolean = false;
    isTeslaUser: boolean = false;
    helpUrl: string;
    showCMTUrl: boolean = false;
    cmtUrl: string;

    constructor(
        private activedRoute: ActivatedRoute,
        private supplierStateService: SupplierStateService,
        public translateService: I18nService,
        private documentService: SupplierDocumentService,
        private store: Store<SupplierState>,
        private dialog: TslDialog,
        private config: AppSettings,
    ) {

    }

    ngOnInit() {
        this.store.select(getGeneralSettings).subscribe(res => {
            this.settings = res;
            if (res) {
                this.showCMTUrl = this.settings.settings.showCMTLink === "1";
                this.cmtUrl = this.settings.settings.cmtUrl;
                this.helpUrl = this.settings.settings.documentHelpUrl;
            }
        });
        this.activedRoute.parent.params.subscribe(params => {
            this.supplierCode = params['code'];
            this.store.dispatch(new GetContractsByCode(this.supplierCode));
            this.store.dispatch(new GetSupplierProfileCurrentUser());
            const user = this.store.pipe(select(SelectCurrentUser)).subscribe(result => {
                if (result) {
                    this.userDetails = result;
                    if (this.userDetails) {
                        if (this.userDetails.supplierContact && this.userDetails.supplierContact.supplierMaps) {
                            let supplierMap: SupplierContactMap = this.userDetails.supplierContact.supplierMaps.find(x => x.supplierCode == this.supplierCode) as SupplierContactMap;
                            this.isPrimary = supplierMap.userRoles.some(x => x.role.toLocaleLowerCase() === "primary");
                            this.isProfileAdmin = supplierMap.userRoles.some(x => x.role.toLocaleLowerCase() === "profile admin");
                        }

                        this.isTeslaUser = this.userDetails.isTeslaUser;
                    }
                }
            });
            this.documentService.getDocumentType().subscribe(res => {
                this.types = res;
                this.loadTable();
            });
            this.wsmUrl = `${this.config.WSM_URL}edit/${this.supplierCode}/document`;
        });
    }

    private loadTable(reload: boolean = false): void {
        if (reload) {
            this.store.dispatch(new ClearSupplierDetailsByCode(this.supplierCode));
        }

        this.store.select(SelectSupplierContracts)
            .subscribe(res => {
                if (res) {
                    this.contracts = res;
                    this.total = res.length;
                    this.onFilter();
                }
            });
    }

    onPageChange(pageChange: PageEvent): void {
        const { pageIndex, pageSize } = pageChange;
        this.pageIndex = pageIndex;
        this.pageSize = pageSize;
        this.onFilter();
    }

    onFilter(): void {
        let items = this.contracts;

        const skip = this.pageIndex * this.pageSize;
        const take = (this.pageIndex + 1) * this.pageSize;
        this.datasource.next(items.slice(skip, take));
    }

    isExpired(item: Contract): boolean {
        if (item && item.expirationDate) {
            const expiryDate = new Date(item.expirationDate);
            return expiryDate.getTime() < Date.now();
        }
        return false;
    }

    getDocumentType(abbreviation: string) {
        if (this.types && abbreviation) {
            var documentTypeData = this.types.find(x => x.name.toLocaleLowerCase() === abbreviation.toLocaleLowerCase());
            if (documentTypeData)
                return documentTypeData.name + " - " + this.translateService.get(documentTypeData.description);
            else
                return abbreviation;
        }
        return "";
    }

    onSortChange(sort: TslSort): void {
        const orderedItems = this.contracts.sort((a, b) => {
            const isAsc = sort.direction === 'asc';

            switch (sort.active) {
                case 'category': return compare(a.abbreviation.toLowerCase(), b.abbreviation.toLowerCase(), isAsc);
                case 'name': return compare(a.contractName.toLowerCase(), b.contractName.toLowerCase(), isAsc);
                case 'start': {
                    const aDate = new Date(a.effectiveDate);
                    const bDate = new Date(b.effectiveDate);
                    return compare(aDate.getTime(), bDate.getTime(), isAsc);
                }
                case 'expiry': {
                    const aDate = new Date(a.expirationDate);
                    const bDate = new Date(b.expirationDate);
                    return compare(aDate.getTime(), bDate.getTime(), isAsc);
                }
                case 'signature': {
                    const aDate = new Date(a.signedDate);
                    const bDate = new Date(b.signedDate);
                    return compare(aDate.getTime(), bDate.getTime(), isAsc);
                }
            }
        });

        const skip = this.pageIndex * this.pageSize;
        const take = (this.pageIndex + 1) * this.pageSize;
        this.datasource.next(orderedItems.slice(skip, take));

        function compare(a: number | string, b: number | string, isAsc: boolean) {
            return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
        }
    }

    onDownloadFile(document: Contract) {
        if (document) {
            this.supplierStateService.downloadFileFromClm(this.supplierCode, document.contractID)
                .subscribe(file => {
                    const fileName: string = document.contractName + ".pdf";
                    FileSaver.saveAs(file, fileName);
                });
        }
    }

    isDirty() {
        return false;
    }
}
